<template>
	<div class="message-inbox">
		<template v-if="selectedMessage.id">
			<MessageHeader
				class="message-inbox__header"
				:avatar="selectedMessage.image"
				:sender="selectedMessage.name"
				:sender-email="selectedMessage.email"
				:message-date="selectedMessage.date_created"
				:message-time="selectedMessage.time_created"
			/>

			<div class="message-inbox__message">
				<h3 class="subject">{{ selectedMessage.type || selectedMessage.subject }}</h3>
				<p class="message">{{ selectedMessage.content }}</p>
			</div>

			<section
				v-if="selectedMessage.reply"
				class="message-inbox__thread"
			>
				<MessageCard
					class="thread"
					sender="Upmood Admin"
					direction="rtl"
					:message="selectedMessage.reply"
					:avatar="require('@/assets/img/default-avatar.png')"
					v-if="selectedMessage.reply"
				>
				</MessageCard>
			</section>

			<template v-else>
				<div class="message-inbox__footer" v-if="!showReply">
					<AppButton
						modifier="small-primary"
						@click="showReply = true"
					>
						Reply
					</AppButton>
				</div>
				<section class="message-inbox__reply" v-else>
					<div class="reply">
						<AppTextEditor
							v-model="reply"
							@image-added="handleImageAdded"
						/>

						<div
							v-if="loading"
							class="message-inbox__loading"
						>
							<AppLoading/>
						</div>
					</div>

					<div class="message-inbox__footer">
						<div class="message-inbox__attachment">
							<MessageAttachment v-for="(item, key) in attachments" :key="key" @remove="removeAttachment(item, key)">
								{{ item.original_name }}
							</MessageAttachment>
						</div>

						<AppFileInput
							:value="null"
							@input="addAttachment"
							:disabled="loading"
						>
							<AppIcon
								class="icon attachment"
								icon-name="clip"
							/>
						</AppFileInput>
						<AppButton
							modifier="icon small-secondary"
							@click="sendReply"
							:disabled="loading"
						>
							<AppIcon
								class="icon"
								icon-name="send"
							/>
						</AppButton>
						<AppButton
							modifier="small-danger"
							@click="discard"
							:disabled="loading"
						>
							Discard
						</AppButton>
					</div>
				</section>
			</template>
		</template>
		<p
			class="empty"
			v-else
		>No selected Message</p>
	</div>
</template>

<script>
import { mapFields } from 'vuex-map-fields'
import MessageHeader from '@/components/messages/MessageHeader.vue'
import MessageCard from '@/components/messages/MessageCard.vue'
import MessageAttachment from '@/components/messages/MessageAttachment.vue'

export default {
	data () {
		return {
			showReply: false
		}
	},

	methods: {
		sendReply () {
			if (this.reply.trim() === '') {
				this.$store.dispatch('toggleBanner', {
					status: true,
					message: 'Please complete input fields.',
					type: 'invalid'
				})
				return
			}

			let deletedImages = this.deletedAttachments.map(i => i.server_name)

			this.tempFiles.forEach(i => {
				if (this.reply.indexOf(i.server_name) === -1) {
					deletedImages.push(i.server_name)
				}
			})

			const form = new FormData()
			form.append('contact_message_id', this.selectedMessage.id)
			form.append('message', this.reply)
			form.append('deleted_image', deletedImages.join())
			form.append('attachment', this.attachments.map(i => i.server_name).join())

			this.$store.dispatch('message/sendReply', form)
		},

		async handleImageAdded ({ file, Editor, cursorLocation, resetUploader }) {
			var form = new FormData()
			form.append('type', 'admin')
			form.append('file', file)
			form.append('module', 'email')
			form.append('original_name', file.name)
			await this.$store.dispatch('message/tempUpload', form)

			const { path, server_name } = this.tempFiles[this.tempFiles.length - 1]
			const url = `${path}${server_name}`
			Editor.insertEmbed(cursorLocation, 'image', url)
			resetUploader()
		},

		async discard (changePage = false, callback = () => {}) {
			let images = this.tempFiles.map(i => i.server_name)
			let files = this.attachments.map(i => i.server_name)
			let deletedFiles = this.deletedAttachments.map(i => i.server_name)
			let form = new FormData()
			form.append('deleted_image', [...images, ...files, ...deletedFiles])

			if (!this.reply && !this.tempFiles.length) await this.$store.dispatch('message/discard', { form, changePage, callback })
			else {
				this.$store.commit('toggleModal', {
					modalName: 'discard-message',
					confirm: async () => {
						await this.$store.dispatch('message/discard', { form, changePage, callback })
						await this.$store.dispatch('closeModal')
					}
				})
			}
			this.showReply = false
		},

		async addAttachment (files) {
			const file = files[0]
			var form = new FormData()
			form.append('type', 'admin')
			form.append('file', file)
			form.append('module', 'email')
			form.append('original_name', file.name)
			await this.$store.dispatch('message/addAttachment', form)
		},

		removeAttachment (item, index) {
			this.attachments.splice(index, 1)
			this.deletedAttachments.push(item)
		}
	},

	computed: {
		...mapFields('message', {
			selectedMessage: 'selectedMessage',
			messagesLoading: 'messagesLoading',
			reply: 'reply',

			tempFiles: 'tempFiles',
			attachments: 'attachments',
			deletedAttachments: 'deletedAttachments',
			loading: 'buttonLoading'
		})
	},

	components: {
		MessageHeader,
		MessageCard,
		MessageAttachment
	}
}
</script>

<style lang="scss" scoped>
.message-inbox {
	height: 100%;
	width: 100%;
	display: flex;
	flex-direction: column;
	background-color: #fff;
	border: 0.5px solid lighten($default-light, 12%);

	&__header {
		padding: 20px;
	}

	&__message {
		flex: 1 1 auto;
		padding: 0 20px;
		height: 50%;
		margin-left: 3.5em;
		margin-bottom: 20px;
		overflow-y: auto;
		word-break: break-all;

		.subject {
			margin-bottom: 1em;
			font-size: 1em;
			font-weight: bold;
			text-transform: capitalize;
		}
	}

	&__thread {
		flex: 1 1 auto;
		padding: 0 20px;
		margin-left: 3.5em;
		margin-bottom: 20px;
		overflow: auto;

		.thread {
			margin-bottom: 20px;

			&:not(:first-child) {
				margin-top: 20px;
			}
		}
	}

	&__reply {
		padding: 0 20px;
		flex: 1 1 auto;

		.reply {
			position: relative;
		}
	}

	&__loading {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 100%;
		background-color: #eee;
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.5;
	}

	&__footer {
		width: 100%;
		overflow: hidden;
		display: flex;
		align-items: center;
		margin: 10px 0;
		padding-right: 20px;

		> *:only-child {
			margin-left: auto;
		}

		> *:not(:first-child) {
			margin-left: 10px;
			margin-bottom: 5px;
		}

		.hide {
			margin-left: 0;
			margin-right: auto;
		}

		.icon {
			width: 15px;
			height: 15px;

			&.attachment {
				color: $secondary;
			}
		}
	}

	&__attachment {
		flex: 1 1 auto;
		display: flex;
		justify-content: flex-start;
		overflow: auto;
		padding-bottom: 5px;

		> * {
			flex-shrink: 0;

			&:not(:first-child) {
				margin-left: 5px;
			}
		}
	}

	.empty {
		font-size: 1em;
		color: $default-light;
		display: flex;
		align-items: center;
		justify-content: center;
		text-align: center;
		height: 100%;
	}
}
</style>
